import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Gem, HandCoins, Receipt, ScrollText } from "lucide-react";
import { api, DOC_LABELS, money } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { EmptyBranchNote } from "@/components/EmptyBranchNote";
import { formatPhone } from "@/lib/phone";

const CARDS = [
  { key: "tax_invoice", label: "Tax Invoices", icon: Receipt },
  { key: "gold_bullion", label: "Bullion Sales", icon: Gem },
  { key: "loan", label: "Loan Agreements", icon: HandCoins, perm: "documents:create" },
  { key: "buying_contract", label: "Buying Contracts", icon: ScrollText },
];

// Dates on the dashboard follow the shop's local day (Melbourne), not the device or UTC.
const localTodayISO = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Melbourne" }).format(new Date());
const isoToDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
};
const dateToISO = (d) => d.toISOString().slice(0, 10);
const shiftDays = (iso, n) => {
  const d = isoToDate(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return dateToISO(d);
};
const monthStartISO = (iso) => `${iso.slice(0, 7)}-01`;
const prettyDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function Dashboard() {
  const { branch, user, can } = useAuth();
  const [stats, setStats] = useState(null);
  const [dueLoans, setDueLoans] = useState([]);
  const showLoans = can("loans:update");
  const canSeeMoney = can("audit:read");
  const [range, setRange] = useState(() => {
    const t = localTodayISO();
    return { from: t, to: t };
  });

  useEffect(() => {
    if (!showLoans) return;
    api
      .get("/loans/due", { params: { branch, days: 7 } })
      .then(({ data }) => setDueLoans(data))
      .catch(() => setDueLoans([]));
  }, [branch, showLoans]);

  useEffect(() => {
    const params = { branch };
    if (canSeeMoney) {
      params.date_from = range.from;
      params.date_to = range.to;
    }
    api
      .get("/documents/stats", { params })
      .then(({ data }) => setStats(data))
      .catch(() => setStats({ by_type: {}, monthly: [], recent: [], active_loans: 0 }));
  }, [branch, canSeeMoney, range.from, range.to]);

  const byType = stats?.by_type || {};

  return (
    <div className="fade-up space-y-8" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {branch}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">
            Hello, {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Issue an invoice, record a loan, or look up an old record.
          </p>
        </div>
        <Link
          to="/new/tax_invoice"
          data-testid="dashboard-new-invoice-button"
          className="bg-[#C5A059] px-5 py-2.5 text-sm font-semibold text-[#0A0A0A] transition-colors duration-150 hover:bg-[#B38F4E]"
        >
          + New Tax Invoice
        </Link>
      </div>

      {canSeeMoney && (
        <div className="border border-border bg-white p-5" data-testid="dashboard-range-picker">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Takings for selected period
              </p>
              <div className="tabular mt-1 text-3xl font-bold sm:text-4xl" data-testid="range-total">
                {money(stats?.range_total || 0)}
              </div>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="range-count">
                {stats?.range_count || 0} record(s) · {prettyDate(range.from)}
                {range.to !== range.from ? ` – ${prettyDate(range.to)}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                From
                <input
                  type="date"
                  value={range.from}
                  max={range.to}
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value || r.from }))}
                  data-testid="range-from-input"
                  className="rounded-none border border-border bg-white px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                To
                <input
                  type="date"
                  value={range.to}
                  min={range.from}
                  max={localTodayISO()}
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value || r.to }))}
                  data-testid="range-to-input"
                  className="rounded-none border border-border bg-white px-3 py-2 text-sm text-foreground"
                />
              </label>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { label: "Today", get: () => { const t = localTodayISO(); return { from: t, to: t }; } },
              { label: "Last 7 days", get: () => { const t = localTodayISO(); return { from: shiftDays(t, -6), to: t }; } },
              { label: "This month", get: () => { const t = localTodayISO(); return { from: monthStartISO(t), to: t }; } },
            ].map((p) => {
              const r = p.get();
              const active = range.from === r.from && range.to === r.to;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setRange(r)}
                  data-testid={`range-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
                    active
                      ? "bg-[#C5A059] text-[#0A0A0A]"
                      : "border border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
        {CARDS.filter((c) => !c.perm || can(c.perm)).map(({ key, label, icon: Icon }) => (
          <div
            key={key}
            className="bg-white p-5 transition-transform duration-150 hover:-translate-y-px"
            data-testid={`stat-card-${key}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                {label}
              </span>
              <Icon className="h-4 w-4 text-[#C5A059]" />
            </div>
            <div className="tabular mt-4 text-2xl font-bold">
              {canSeeMoney ? money(byType[key]?.total || 0) : (byType[key]?.count || 0)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {canSeeMoney ? `${byType[key]?.count || 0} record(s)` : "record(s) this period"}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="border border-border p-5 lg:col-span-2" data-testid="monthly-chart">
          <h2 className="text-base font-bold md:text-lg">
            {canSeeMoney ? "Monthly value issued" : "Monthly records issued"}
          </h2>
          <div className="mt-6 h-[280px]">
            {stats?.monthly?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthly}>
                  <CartesianGrid stroke="#EEE" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#999" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#999" />
                  <Tooltip formatter={(v) => (canSeeMoney ? money(v) : `${v} record(s)`)} />
                  <Bar dataKey={canSeeMoney ? "sales" : "count"} fill="#C5A059" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6">
                <p className="text-sm text-muted-foreground">
                  No records yet for {branch}. Create your first invoice.
                </p>
                <EmptyBranchNote />
              </div>
            )}
          </div>
        </div>

        <div className="border border-border" data-testid="recent-list">
          <div className="flex items-center justify-between border-b border-border bg-[#F8F9FA] px-4 py-3">
            <h2 className="text-sm font-bold uppercase tracking-wider">Recent</h2>
            <Link to="/documents" className="text-xs text-[#B38F4E] hover:underline">
              View all
            </Link>
          </div>
          <div className="divide-y divide-border">
            {(stats?.recent || []).length === 0 && (
              <p className="px-4 py-6 text-sm text-muted-foreground">Nothing yet.</p>
            )}
            {(stats?.recent || []).map((d) => (
              <Link
                key={d.id}
                to={`/documents/${d.id}`}
                className="block px-4 py-3 transition-colors duration-150 hover:bg-[#FBF8F2]"
                data-testid={`recent-item-${d.doc_number}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-semibold">{d.customer_name}</span>
                  {canSeeMoney && <span className="tabular text-sm">{money(d.total)}</span>}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {DOC_LABELS[d.doc_type]} · #{d.doc_number} · {d.doc_date}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {showLoans && (
        <div className="border border-border bg-[#F8F9FA] px-5 py-4 text-sm" data-testid="active-loans-banner">
          <span className="font-semibold">{stats?.active_loans || 0}</span> active pledge
          loan(s) currently outstanding at {branch}.
        </div>
      )}

      {showLoans && (
      <div className="border border-border" data-testid="loans-due-panel">
        <div className="flex items-center justify-between border-b border-border bg-[#F8F9FA] px-4 py-3">
          <h2 className="text-sm font-bold uppercase tracking-wider">
            Loans due within 7 days
          </h2>
          <span className="text-xs text-muted-foreground">
            {dueLoans.length} to follow up
          </span>
        </div>
        <div className="divide-y divide-border">
          {dueLoans.length === 0 && (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              No pledge loans falling due in the next 7 days.
            </p>
          )}
          {dueLoans.map((l) => (
            <Link
              key={l.id}
              to={`/documents/${l.id}`}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[#FBF8F2]"
              data-testid={`due-loan-${l.doc_number}`}
            >
              <div>
                <div className="text-sm font-semibold">
                  {l.customer_name}{" "}
                  <span className="font-normal text-muted-foreground">
                    · Loan #{l.doc_number} · {l.customer_phone ? formatPhone(l.customer_phone) : "no phone"}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Due {l.end_date} · {money(l.loan_amount)} · {l.branch}
                </div>
              </div>
              <span
                className={`px-2.5 py-1 text-xs font-semibold ${
                  l.overdue
                    ? "bg-[#9A3412] text-white"
                    : "bg-[#C5A059] text-[#0A0A0A]"
                }`}
              >
                {l.overdue
                  ? `${Math.abs(l.days_left)} day(s) overdue`
                  : `${l.days_left} day(s) left`}
              </span>
            </Link>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
